/* eslint-disable react/jsx-one-expression-per-line */
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Chip,
  Icon,
  InlineMessage,
  Link,
  Typography,
} from '@saviynt/design-system';
import moment from 'moment';
import PropTypes from 'prop-types';

import { SANITIZED_DATE_FORMATS_MAPPING } from '../../../../utils/constant';
import { useInvertedLocalize } from '../../../../utils/localization';
import { getMessageKey } from '../../../../utils/utils';
import { LIST_SP_TAB_HISTORY } from '../constant';

import { FieldSet, HistoryCard } from './CertsListSidePanelBodyCommon';
import {
  HISTORY_COLORS_MAPPING,
  HISTORY_ICONS_MAPPING,
  VALID_HISTORY_ACTIONS,
} from './constant';
import { invertedMessages } from './messages';

import './CertsListSidePanelBody.css';

function CertsListSidePanelBodySummary({
  summaryData,
  historyData,
  isAdminLogin,
  globalDateFormat,
  setActiveTab,
  isPrimaryCertifierActive,
  isAutoRevoke,
}) {
  const intl = useIntl();
  const l = useInvertedLocalize(invertedMessages);
  const { messages } = useSelector((state) => state?.user);
  const [isAutoRevokeOn, setIsAutoRevokeOn] = useState(isAutoRevoke);
  const filteredHistoryRecords = historyData?.filter((historyItem) =>
    VALID_HISTORY_ACTIONS.includes(
      historyItem?.action.replace(/\s+/g, '').toLowerCase()
    )
  );
  const isLatestActivity = Boolean(
    filteredHistoryRecords !== null &&
      Array.isArray(filteredHistoryRecords) &&
      filteredHistoryRecords?.length > 0
  );

  const sceondaryCertifierText = summaryData?.secondaryCertifiers
    ?.map((certifier) => certifier?.trim())
    ?.join(', ');

  const getLatestHistory = (historyRecord) => {
    const historyAction = historyRecord?.action
      .replace(/\s+/g, '')
      .toLowerCase();
    const isReassignAction = Boolean(
      historyAction === 'reassignedcertification' ||
        historyAction === 'reassigned'
    );

    const HISTORY_USER_ACTION_LABEL_MAPPING = {
      certificationdiscontinued:
        messages.CERTS_CANCELLED_BY ||
        localize(intl, getMessageKey('CERTS_CANCELLED_BY')),
      canceled:
        messages.CERTS_CANCELLED_BY ||
        localize(intl, getMessageKey('CERTS_CANCELLED_BY')),
      certificationexported:
        messages.CERTS_EXPORTED_BY ||
        localize(intl, getMessageKey('CERTS_EXPORTED_BY')),
      exported:
        messages.CERTS_EXPORTED_BY ||
        localize(intl, getMessageKey('CERTS_EXPORTED_BY')),
      reassignedcertification:
        messages.CERTS_REASSIGNED_FROM ||
        localize(intl, getMessageKey('CERTS_REASSIGNED_FROM')),
      reassigned:
        messages.CERTS_REASSIGNED_FROM ||
        localize(intl, getMessageKey('CERTS_REASSIGNED_FROM')),
      certificationlocked:
        messages.CERTS_COMPLETED_BY ||
        localize(intl, getMessageKey('CERTS_COMPLETED_BY')),
      completed:
        messages.CERTS_COMPLETED_BY ||
        localize(intl, getMessageKey('CERTS_COMPLETED_BY')),
      default:
        messages.CERTS_ACTIONED_BY ||
        localize(intl, getMessageKey('CERTS_ACTIONED_BY')),
    };

    const HISTORY_DATE_ACTION_LABEL_MAPPING = {
      certificationdiscontinued:
        messages.CERTS_CANCELLED_ON ||
        localize(intl, getMessageKey('CERTS_CANCELLED_ON')),
      canceled:
        messages.CERTS_CANCELLED_ON ||
        localize(intl, getMessageKey('CERTS_CANCELLED_ON')),
      certificationexported:
        messages.CERTS_EXPORTED_ON ||
        localize(intl, getMessageKey('CERTS_EXPORTED_ON')),
      exported:
        messages.CERTS_EXPORTED_ON ||
        localize(intl, getMessageKey('CERTS_EXPORTED_ON')),
      reassignedcertification:
        messages.CERTS_REASSIGNED_ON ||
        localize(intl, getMessageKey('CERTS_REASSIGNED_ON')),
      reassigned:
        messages.CERTS_REASSIGNED_ON ||
        localize(intl, getMessageKey('CERTS_REASSIGNED_ON')),
      certificationlocked:
        messages.CERTS_COMPLETED_ON ||
        localize(intl, getMessageKey('CERTS_COMPLETED_ON')),
      completed:
        messages.CERTS_COMPLETED_ON ||
        localize(intl, getMessageKey('CERTS_COMPLETED_ON')),
      default:
        messages.CERTS_ACTIONED_BY ||
        localize(intl, getMessageKey('CERTS_ACTIONED_ON')),
    };

    return (
      <HistoryCard
        title={l(historyRecord?.actionMessage)}
        timelineColor={
          HISTORY_COLORS_MAPPING[historyAction] ||
          HISTORY_COLORS_MAPPING.default
        }
        timelineKind={
          HISTORY_ICONS_MAPPING[historyAction] || HISTORY_ICONS_MAPPING.default
        }
        isTailVisible={false}>
        {isReassignAction ? (
          <div className='CertsListSidePanelBodyHistory-fieldsetWrapper--isVertical'>
            <FieldSet
              title={
                messages.CERTS_REASSIGNED_FROM ||
                localize(intl, getMessageKey('CERTS_REASSIGNED_FROM'))
              }
              value={`${historyRecord?.reassignedFrom}`}
              lineLimitValue={1}
            />
            <FieldSet
              title={
                messages.CERTS_REASSIGNED_TO ||
                localize(intl, getMessageKey('CERTS_REASSIGNED_TO'))
              }
              value={`${historyRecord?.reassignedTo}`}
              lineLimitValue={1}
            />
          </div>
        ) : (
          <FieldSet
            title={
              HISTORY_USER_ACTION_LABEL_MAPPING[historyAction] ||
              HISTORY_USER_ACTION_LABEL_MAPPING.default
            }
            value={`${historyRecord?.actionUser}`}
            lineLimitValue={1}
            isGapRequired={false}
          />
        )}
        <FieldSet
          title={
            HISTORY_DATE_ACTION_LABEL_MAPPING[historyAction] ||
            HISTORY_DATE_ACTION_LABEL_MAPPING.default
          }
          value={moment
            .utc(
              historyRecord?.actionDate,
              SANITIZED_DATE_FORMATS_MAPPING?.[historyRecord?.actionDateFormat]
            )
            .local()
            .format(`${globalDateFormat?.toUpperCase()} hh:mm:ss`)}
          isGapRequired={false}
        />
        {historyRecord?.comment?.trim()?.length > 0 && (
          <FieldSet
            title={
              messages.CERTS_COMMENTS ||
              localize(intl, getMessageKey('CERTS_COMMENTS'))
            }
            value={`${historyRecord?.comment}`}
            lineLimitValue={2}
            isGapRequired={false}
          />
        )}
      </HistoryCard>
    );
  };

  return (
    <>
      {/* My Role is not suppported through APIs */}
      {/* <div className='CertsListSidePanelBodySummary-fieldsetWrapper'>
        <FieldSet title='My Role' value='WIP: Secondary Certifier' />
      </div> */}
      <div className='CertsListSidePanelBodySummary-fieldsetWrapper'>
        <div className='CertsListSidePanelBodySummary-fieldsetWrapper CertsListSidePanelBodySummary-fieldsetWrapper--isVertical'>
          <FieldSet
            title={
              messages.CERTS_LIST_START_DATE ||
              localize(intl, getMessageKey('CERTS_LIST_START_DATE'))
            }
            value={moment(summaryData?.startDate)
              .local()
              .format(`${globalDateFormat?.toUpperCase()}`)}
          />
          <FieldSet
            title={
              messages.CERTS_LIST_DUE_DATE ||
              localize(intl, getMessageKey('CERTS_LIST_DUE_DATE'))
            }
            value={moment(summaryData?.endDate)
              .local()
              .format(`${globalDateFormat?.toUpperCase()}`)}
          />
        </div>
        {!isAdminLogin && (
          <InlineMessage
            kind='background'
            colorTheme='warning'
            leftIcon={<Icon kind='AlertWarningFilled' />}
            text={
              messages.CERTS_LIST_SUMMARY_WARN ||
              localize(intl, getMessageKey('CERTS_LIST_SUMMARY_WARN'))
            }
            expandingContent={
              <Typography
                kind='body2'
                desktop='body2-d'
                mobile='body2-d'
                color='--color-foreground-warning-bold'>
                <br />{' '}
                {messages.CERTS_SUMMARY_INLINE_MSG ||
                  localize(intl, getMessageKey('CERTS_SUMMARY_INLINE_MSG'))}
                <br />
                &nbsp;&nbsp;&middot;&nbsp;&nbsp;&nbsp;
                {messages['Terminated.v2.label.ATTESTATIONS'] ||
                  localize(
                    intl,
                    getMessageKey('Terminated.v2.label.ATTESTATIONS')
                  )}{' '}
                <br />
                &nbsp;&nbsp;&middot;&nbsp;&nbsp;&nbsp;
                {messages.CERTS_UNRESPONDED ||
                  localize(intl, getMessageKey('CERTS_UNRESPONDED'))}{' '}
                <br />
                &nbsp;&nbsp;&middot;&nbsp;&nbsp;&nbsp;
                {messages.Approved || localize(intl, getMessageKey('Approved'))}
              </Typography>
            }
            expandButtonLabels={{
              expanded:
                messages.CERTS_SHOW_LESS ||
                localize(intl, getMessageKey('CERTS_SHOW_LESS')),
              collapsed:
                messages.CERTS_SHOW_MORE ||
                localize(intl, getMessageKey('CERTS_SHOW_MORE')),
            }}
            isVisible={isAutoRevokeOn}
            onDismiss={() => setIsAutoRevokeOn(false)}
            isDismissible
          />
        )}
      </div>
      <div className='CertsListSidePanelBodySummary-fieldsetWrapper'>
        {/* Secondary certifiers are not supported by APIs */}
        {summaryData?.primaryCertifier && (
          <FieldSet
            title={
              messages.CERTS_LIST_PRIMARY_CERTIFIER ||
              localize(intl, getMessageKey('CERTS_LIST_PRIMARY_CERTIFIER'))
            }
            value={`${summaryData?.primaryCertifier}`}
            lineLimitValue={2}
            suffixComponent={
              !isPrimaryCertifierActive ? (
                <Chip
                  kind='subtleFilled'
                  label={
                    messages.CERTS_LIST_INACTIVE ||
                    localize(intl, getMessageKey('CERTS_LIST_INACTIVE'))
                  }
                  color='warning'
                  size='xSmall'
                />
              ) : null
            }
          />
        )}
        {sceondaryCertifierText && (
          <FieldSet
            title={
              messages.CERTS_SECONDARY_CERTIFIER ||
              localize(intl, getMessageKey('CERTS_SECONDARY_CERTIFIER'))
            }
            value={sceondaryCertifierText}
            lineLimitValue={2}
          />
        )}
      </div>
      {isLatestActivity && (
        <div className='CertsListSidePanelBodySummary-latestActivity'>
          <Typography
            kind='body2-bold'
            desktop='body2-bold-d'
            mobile='body2-bold-d'>
            {messages.CERTS_LATEST_ACTIVITY ||
              localize(intl, getMessageKey('CERTS_LATEST_ACTIVITY'))}
          </Typography>
          {getLatestHistory(filteredHistoryRecords[0])}
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
          <Link
            kind='button'
            text={
              messages.CERTS_VIEW_HISTORY ||
              localize(intl, getMessageKey('CERTS_VIEW_HISTORY'))
            }
            size='medium'
            onClick={() => setActiveTab(LIST_SP_TAB_HISTORY)}
          />
        </div>
      )}
    </>
  );
}

CertsListSidePanelBodySummary.propTypes = {
  summaryData: PropTypes.shape({
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    primaryCertifier: PropTypes.string,
    secondaryCertifiers: PropTypes.arrayOf(PropTypes.string),
  }),
  historyData: PropTypes.arrayOf(
    PropTypes.shape({
      action: PropTypes.string,
      actionUser: PropTypes.string,
      actionDate: PropTypes.string,
      actionDateFormat: PropTypes.string,
      actionMessage: PropTypes.string,
      comment: PropTypes.string,
    })
  ),
  isAdminLogin: PropTypes.bool.isRequired,
  globalDateFormat: PropTypes.string.isRequired,
  isPrimaryCertifierActive: PropTypes.bool,
  setActiveTab: PropTypes.func,
  isAutoRevoke: PropTypes.bool,
};

CertsListSidePanelBodySummary.defaultProps = {
  summaryData: {},
  historyData: null,
  isPrimaryCertifierActive: true,
  setActiveTab: () => {},
  isAutoRevoke: false,
};

export default CertsListSidePanelBodySummary;
